import React, { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '../ui/Button';
import { Label } from '../ui/label';
import {
  NINJA_SYNC_INTERVAL_OPTIONS,
  deviceSyncAgeLabel,
  formatApiInstant,
  isAllowedSyncInterval,
  isDeviceSyncStale,
  syncIntervalOption,
} from '../../services/rmmSyncSchedule';

interface Props {
  lastSyncedAt?: string | null;
  syncIntervalHours?: number | null;
  /** Core included `syncIntervalHours` on the config payload, so the interval can be saved. */
  scheduleSupported?: boolean;
  nextSyncAt?: string | null;
  syncing?: boolean;
  savingInterval?: boolean;
  syncMessage?: string | null;
  onSync: () => void;
  onSaveInterval?: (hours: number) => void;
  /** Customer page: pull now, without the partner-wide interval editor. */
  compact?: boolean;
  now?: number;
}

const fmtDate = (iso?: string | null) => formatApiInstant(iso);

export function NinjaDeviceSyncStatus({
  lastSyncedAt,
  syncIntervalHours,
  scheduleSupported = false,
  nextSyncAt,
  syncing = false,
  savingInterval = false,
  syncMessage,
  onSync,
  onSaveInterval,
  compact = false,
  now = Date.now(),
}: Props) {
  const stale = isDeviceSyncStale(lastSyncedAt, now, scheduleSupported ? syncIntervalHours : undefined);
  const age = deviceSyncAgeLabel(lastSyncedAt, now);
  const absolute = fmtDate(lastSyncedAt);
  const [draft, setDraft] = useState<number | ''>(syncIntervalHours && syncIntervalHours > 0 ? syncIntervalHours : '');

  useEffect(() => {
    setDraft(syncIntervalHours && syncIntervalHours > 0 ? syncIntervalHours : '');
  }, [syncIntervalHours]);

  const selected = typeof draft === 'number' ? syncIntervalOption(draft) : null;
  const extraOption =
    typeof draft === 'number' && !isAllowedSyncInterval(draft)
      ? { hours: draft, label: `Every ${draft} hours (current)` }
      : null;
  const canSave =
    scheduleSupported &&
    typeof draft === 'number' &&
    isAllowedSyncInterval(draft) &&
    draft !== syncIntervalHours &&
    !savingInterval;

  return (
    <div
      className={`rounded-lg border p-4 ${
        stale ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="text-sm font-medium text-slate-900">Device list</div>
          {stale ? (
            <p className="text-sm text-amber-800">
              {lastSyncedAt
                ? `The last successful pull was ${age}${absolute ? ` (${absolute})` : ''}. Sync now to pull the current device list from NinjaOne.`
                : 'NinjaOne devices have not been pulled yet. Sync now to pull the device list.'}
            </p>
          ) : (
            <p className="text-sm text-slate-600">
              Last pull {age}
              {absolute ? ` (${absolute})` : ''}.
            </p>
          )}
          {nextSyncAt && (
            <p className="text-xs text-slate-500">Next automatic pull {fmtDate(nextSyncAt)}.</p>
          )}
        </div>
        <Button type="button" variant={stale ? 'default' : 'outline'} onClick={onSync} disabled={syncing}>
          <RefreshCw className={`h-4 w-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing…' : 'Sync now'}
        </Button>
      </div>

      {syncMessage && (
        <p className="mt-3 text-sm text-emerald-700" aria-live="polite">
          {syncMessage}
        </p>
      )}

      {!compact && scheduleSupported && (
        <div className="mt-4 grid gap-2 sm:max-w-md">
          <Label htmlFor="ninja-sync-interval">Automatic pull</Label>
          <div className="flex flex-wrap items-center gap-2">
            <select
              id="ninja-sync-interval"
              value={draft === '' ? '' : String(draft)}
              onChange={(e) => setDraft(e.target.value ? Number(e.target.value) : '')}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="" disabled>
                Choose an interval
              </option>
              {extraOption && (
                <option value={extraOption.hours}>{extraOption.label}</option>
              )}
              {NINJA_SYNC_INTERVAL_OPTIONS.map((option) => (
                <option key={option.hours} value={option.hours}>
                  {option.label}
                </option>
              ))}
            </select>
            <Button
              type="button"
              variant="secondary"
              onClick={() => typeof draft === 'number' && onSaveInterval?.(draft)}
              disabled={!canSave}
            >
              {savingInterval ? 'Saving…' : 'Save interval'}
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            {selected?.detail ??
              'A full device list is a heavy pull. Every 6 hours is the shortest interval. Once a day suits most partners.'}
          </p>
        </div>
      )}

      {!compact && !scheduleSupported && (
        <p className="mt-3 text-xs text-slate-500">
          Automatic pulls run on Aitryx’s schedule. The interval control appears here once this connection
          reports one. Sync now pulls the current device list straight away.
        </p>
      )}
    </div>
  );
}
